import { getCurrentInstance } from 'vue'

import { useNotificationStore } from '@/stores/notification'

import type { Snackbar } from '@/stores/notification'

export interface CopyOptions {
  text: string
  success?: string
  error?: string
  element?: Element
  snackbarType?: keyof Snackbar
}

export interface UseClipboardReturnValue {
  /**
   * Copies the given text to the clipboard.
   */
  copy: (options: CopyOptions | string) => Promise<void>
  /**
   * The current value of the clipboard.
   */
  paste: () => Promise<string>
}

export function useClipboard (): UseClipboardReturnValue {
  const notificationStore = useNotificationStore()
  const vm = getCurrentInstance()

  const paste: UseClipboardReturnValue['paste'] = async () => {
    if (!navigator.clipboard) return ''
    try {
      return await navigator.clipboard.readText()
    } catch {
      notificationStore.snackbar.error('Failed to read from clipboard.')
      return ''
    }
  }

  const copy: UseClipboardReturnValue['copy'] = async (options) => {
    const {
      text,
      success = 'Copied to clipboard.',
      error = 'Copy failed.',
      element,
      snackbarType = 'success',
    } = typeof options === 'string'
      ? { text: options }
      : options

    if (!text) {
      notificationStore.snackbar.error('No text provided to copy.')
      return
    }

    try {
      if (!navigator.clipboard) {
        legacyCopy({ text, success, error, element, snackbarType })
      } else {
        await navigator.clipboard.writeText(text)
      }
      notificationStore.snackbar[snackbarType](success)
    } catch {
      notificationStore.snackbar.error(error)
    }
  }

  // Legacy clipboard function for compatibility
  function legacyCopy ({ text, success = 'Copied to clipboard.', error = 'Copy failed.', element, snackbarType = 'success' }: CopyOptions): void {
    const textArea = document.createElement('textarea')
    textArea.value = text

    textArea.setAttribute('readonly', '')
    textArea.style.position = 'fixed'
    textArea.style.pointerEvents = 'none'
    textArea.style.zIndex = '-9999'
    textArea.style.opacity = '0'
    textArea.style.top = '0'
    textArea.style.left = '0'

    /* istanbul ignore if: cannot append to $el in test env */
    if (!element) {
      element = vm?.proxy!.$el
      if (!element) throw new Error('Copy command failed')
    }

    element.appendChild(textArea)

    textArea.select()

    if (!document.execCommand('copy')) {
      throw new Error('Copy command failed')
    }

    element?.removeChild(textArea)
  }

  return {
    copy,
    paste,
  }
}
